import { useQuery } from '@tanstack/react-query';
import type { ApiException } from '@/lib/api/types';
import * as tradingApi from './api';
import type {
  AnalysisPolicy,
  JournalPage,
  JournalStats,
  Recommendation,
  TradingProfile,
} from './types';

/**
 * 단기매매 조회 훅.
 *
 * 갱신 주기를 짧게 잡지 않는다. 추천은 하루 한 번(평일 21:05 자동 종목 선별) 만들어지고
 * 채점은 그보다 더 드물다. 자주 다시 읽어봐야 같은 값이고 홈서버에 부하만 준다.
 * (00-architecture.md 5절 배치 스케줄)
 */

/** 5분. 자동 종목 선별 주기에 비하면 충분히 짧다. */
const STALE_TIME = 5 * 60 * 1000;

export function useRecommendations(days: number, profile?: TradingProfile) {
  return useQuery<Recommendation[], ApiException>({
    // 프로필이 키에 들어간다. 탭을 옮기면 다른 질의가 되고 캐시도 따로 남는다.
    queryKey: ['trading', 'recommendations', days, profile ?? 'ALL'],
    queryFn: () => tradingApi.fetchRecommendations(days, profile),
    staleTime: STALE_TIME,
  });
}

export function useRecommendation(id: number) {
  return useQuery<Recommendation, ApiException>({
    queryKey: ['trading', 'recommendation', id],
    queryFn: () => tradingApi.fetchRecommendation(id),
    staleTime: STALE_TIME,
    // id 가 숫자로 파싱되지 않으면 부르지 않는다. 주소창에 아무 값이나 칠 수 있다.
    enabled: Number.isFinite(id),
  });
}

export function useJournal(page: number, size: number, profile?: TradingProfile) {
  return useQuery<JournalPage, ApiException>({
    queryKey: ['trading', 'journal', page, size, profile ?? 'ALL'],
    queryFn: () => tradingApi.fetchJournal(page, size, profile),
    staleTime: STALE_TIME,
    // 페이지를 넘길 때 목록이 사라졌다 나타나지 않게 이전 데이터를 유지한다.
    placeholderData: (previous) => previous,
  });
}

export function useJournalStats(profile?: TradingProfile) {
  return useQuery<JournalStats, ApiException>({
    queryKey: ['trading', 'journal', 'stats', profile ?? 'ALL'],
    queryFn: () => tradingApi.fetchJournalStats(profile),
    staleTime: STALE_TIME,
  });
}

/**
 * AI 분석 기준. (3.12)
 *
 * 서버에서 거의 바뀌지 않는 값이라 오래 캐시한다.
 * 다만 무한히 두지는 않는다 — 배포로 프롬프트가 바뀌면 따라가야 한다.
 */
export function useAnalysisPolicy() {
  return useQuery<AnalysisPolicy, ApiException>({
    queryKey: ['trading', 'analysis-policy'],
    queryFn: tradingApi.fetchAnalysisPolicy,
    staleTime: 30 * 60_000,
  });
}
